import {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { summariseSupervisors } from "./supervisor-summary";
import {
  CalendarRange,
  initialCalendarRange,
  normaliseCalendarRange,
  rangeForView,
} from "./calendar-range";
import { toResourceEvents, toResources, withEmptyLanes } from "./resource-events";
import { Calendar, momentLocalizer, View, Views } from "react-big-calendar";
import { OverlayPanel } from "primereact/overlaypanel";
import { MultiSelect } from "primereact/multiselect";
import { SelectButton } from "primereact/selectbutton";
import moment from "moment";
import { classNames as cx } from "primereact/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";

import { SectionHeader } from "../../../components/SectionHeader/SectionHeader";
import { requests } from "../../../services/http-common";
import { useToast } from "../../../Providers/ToastProvider";
import { Employee, JOB_ASSIGNABLE_ROLES } from "../../../models/Employee";
import {
  employeesInRoles,
  useAllEmployeeOptions,
} from "../../../hooks/useEmployeeOptions";
import TimeTrackingModal from "../TimeTrackingModal/TimeTrackingModal";
import { CalendarCustomEventType } from "../../../models/JobZone";
import ScheduleInfo from "../ScheduleInfo/ScheduleInfo";

import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import "react-big-calendar/lib/css/react-big-calendar.css";

import { ConfirmationModal } from "../../../components/ConfirmationModal/ConfirmationModal";
import { Button } from "primereact/button";
import styles from "./Schedule.module.scss";
import "./Calendar.scss";
import { CreateJobModal } from "./CreateJobModal/CreateJobModal";
import { ScheduleSearch } from "./ScheduleSearch/ScheduleSearch";
import { useUser } from "../../../keycloak";
import { UserRoles } from "../../../constants/userRoles";

const localizer = momentLocalizer(moment);
const DnDCalendar = withDragAndDrop(Calendar);

interface ISchedule {
  startTime: string;
  endTime: string;
  id: number;
  employees: Employee[];
  /**
   * Whole user rows, not just names: `createScheduleResponseDto` partitions a
   * schedule's users by `isSupervisor` and returns both halves as full user
   * objects. Typing this as a name pair threw the ids away, and an id is the
   * only handle the "By employee" view has for putting a shift on a row.
   */
  supervisor: Employee[];
}

interface IJob {
  jobSchedules: ISchedule[];
  id: number;
  /**
   * Nullable in practice: `jobDetail.leadId` carries no foreign key and
   * `leadService.deleteLead` hard-deletes, so a job can outlive its lead and
   * arrive here with no `lead` at all.
   */
  lead: {
    address: string;
  } | null;
  name: string;
}

/**
 * The only views react-big-calendar can draw resource lanes in: `resources`
 * is read by `TimeGrid` alone, so month and agenda ignore it silently.
 */
const RESOURCE_VIEWS: View[] = [Views.WEEK, Views.DAY];

/** What the calendar is actually told to show, given the layout toggle. */
const effectiveViewFor = (byEmployee: boolean, view: View): View =>
  byEmployee && !RESOURCE_VIEWS.includes(view) ? Views.WEEK : view;

function Schedule() {
  const [events, setEvents] = useState<CalendarCustomEventType[]>([]);
  const [showDeletePopup, setShowDeletePopup] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>("");
  /** Empty means everyone, not no one. */
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<string[]>([]);
  const [byEmployee, setByEmployee] = useState<boolean>(false);
  /**
   * The view the user picked, which is not always the one on screen: turning
   * "By employee" on while a month is open shows a week, because only the
   * time grid draws lanes. Keeping the choice here rather than overwriting it
   * is what lets turning the toggle back off land on the month again.
   */
  const [view, setView] = useState<View>(Views.MONTH);
  /**
   * The calendar's date is controlled so that a view change made from here
   * can work out which window the new view covers.
   */
  const [date, setDate] = useState<Date>(() => new Date());
  const [showTrackingModal, setShowTrackingModal] = useState<boolean>(false);
  const [showCreateJobModal, setShowCreateJobModal] = useState<boolean>(false);
  const user: any = useUser();

  const [selectedEvent, setSelectedEvent] =
    useState<CalendarCustomEventType | null>(null);

  const eventPopover = useRef(null);
  const { show } = useToast();

  // The window the calendar is showing. It lives in state because
  // `onRangeChange` does not fire on mount, so the first window has to be
  // computed rather than waited for; and because `/job/calendar` is
  // unpaginated, the window is the only thing bounding the payload.
  const [range, setRange] = useState<CalendarRange>(() =>
    initialCalendarRange(new Date())
  );

  const queryClient = useQueryClient();

  /**
   * Drop every cached `/job/calendar` window after a schedule changes.
   *
   * The optimistic `setEvents` below only corrects the window on screen, and
   * the query key carries that window, so every other window still holds the
   * schedule as it was before the change. Month grids run whole weeks and so
   * overlap their neighbours - a schedule near a month edge sits in more than
   * one cached window - which is why this cannot stop at the active one.
   *
   * `invalidateQueries` alone would leave those windows cached and merely
   * stale, so paging to one would paint the old event until its refetch
   * lands, and would keep painting it if that refetch failed. Removing them
   * makes the staleness unservable: the visible window refetches, the rest
   * are fetched afresh when they are next opened.
   */
  const refreshSchedule = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["jobs-data"] });
    queryClient.removeQueries({ queryKey: ["jobs-data"], type: "inactive" });
  }, [queryClient]);

  const handleRangeChange = useCallback(
    (next: Date[] | { start: Date; end: Date }) => {
      setRange(normaliseCalendarRange(next));
    },
    []
  );

  const { data: jobsData } = useQuery({
    // `jobs-data` stays the key prefix so any invalidateQueries on it still
    // matches; it is not referenced anywhere else in the app today.
    queryKey: [`jobs-data`, range.start.toISOString(), range.end.toISOString()],
    queryFn: () => {
      return requests.get(
        `/job/calendar?start=${encodeURIComponent(
          range.start.toISOString()
        )}&end=${encodeURIComponent(range.end.toISOString())}`
      );
    },
    // A failed fetch leaves `jobsData` undefined, the effect below skips, and
    // the previous window's events stay on screen - which looks exactly like
    // the missing-jobs bug this endpoint exists to fix. Say so instead.
    onError: (error: any) => {
      show({
        severity: "error",
        detail:
          error.response?.data?.error?.message ||
          "Could not load the schedule for these dates ",
      });
    },
  });

  const deleteSchedule = useMutation({
    mutationFn: (scheduleId: any) => {
      return requests.delete(`/job/jobSchedule/${scheduleId}`);
    },
    onSuccess: (_, id) => {
      // Kept alongside the refetch: the local filter takes the event off the
      // calendar on the same frame, where the round trip would leave it
      // sitting there until the response lands.
      setEvents((prev) => prev.filter((e) => e.id !== id));
      refreshSchedule();
      show({ detail: "Schedule was deleted successfully !" });
      setSelectedEvent(null);
    },
    onError: (error: any) => {
      show({
        severity: "error",
        detail: error.response?.data?.error?.message || "Something went wrong ",
      });
    },
  });

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const { employees: allEmployees, options: allEmployeeOptions } =
    useAllEmployeeOptions();

  /** The same people the job form offers when assigning a schedule. */
  const employeeOptions = useMemo(
    () => employeesInRoles(allEmployeeOptions, JOB_ASSIGNABLE_ROLES),
    [allEmployeeOptions]
  );

  /**
   * The explicit filter, applied before the loose search so the two compose:
   * the search narrows what the filter let through, never the other way
   * round.
   *
   * Membership is decided by `toResourceEvents`, not by a second reading of
   * the crew here, so the two layouts can never disagree about who is on a
   * shift - in particular, a supervisor counts as being on it.
   */
  const employeeFilteredEvents = useMemo(() => {
    if (!selectedEmployeeIds.length) {
      return events;
    }

    return events.filter(
      (event) => toResourceEvents([event], selectedEmployeeIds).length > 0
    );
  }, [events, selectedEmployeeIds]);

  const filteredEvents = useMemo(() => {
    const events = employeeFilteredEvents;

    if (searchValue.trim()) {
      const clearValue = searchValue.trim().toLowerCase();
      const filtered = events.filter((obj: CalendarCustomEventType) => {
        // Check if the title includes the search title
        const titleMatches = (obj.title as any)
          .toLowerCase()
          .includes(clearValue);

        // Check if any employee's firstName or lastName matches the search criteria
        const employeeMatches = obj.employees.some((employee) => {
          return (
            employee.firstName.toLowerCase().includes(clearValue) ||
            employee.lastName.toLowerCase().includes(clearValue)
          );
        });

        // Return true if either the title or employee matches
        return titleMatches || employeeMatches;
      });

      return filtered;
    }

    return events;
  }, [searchValue, employeeFilteredEvents]);

  /**
   * One event per (shift, person) pair, built at render and never put into
   * `events`.
   *
   * `onEventDrop` and the delete both key off `ev.id`, and every fanned copy
   * of a shift keeps that shift's own id - so storing them would make one
   * drag rewrite, or one delete remove, every copy of the shift at once.
   */
  const resourceEvents = useMemo(
    () => toResourceEvents(filteredEvents, selectedEmployeeIds),
    [filteredEvents, selectedEmployeeIds]
  );

  /**
   * The lanes. Every explicitly selected employee gets one whether or not
   * they have a shift in the window: an empty lane is the answer to "is this
   * person free?", which is most of what the view is for. With nothing
   * selected the lanes are just whoever is working.
   */
  const resources = useMemo(
    () =>
      withEmptyLanes(
        toResources(filteredEvents, selectedEmployeeIds),
        selectedEmployeeIds,
        allEmployees
      ),
    [filteredEvents, selectedEmployeeIds, allEmployees]
  );

  const effectiveView = effectiveViewFor(byEmployee, view);

  /**
   * Turning the toggle on or off can move the view, and react-big-calendar
   * only reports a new window for view changes it made itself, so the window
   * is recomputed here when the view moves underneath it.
   */
  const handleLayoutChange = useCallback(
    (next: boolean) => {
      const nextView = effectiveViewFor(next, view);

      if (nextView !== effectiveViewFor(byEmployee, view)) {
        setRange(rangeForView(nextView, date));
      }

      setByEmployee(next);
    },
    [byEmployee, view, date]
  );

  useEffect(() => {
    if (jobsData) {
      const schedules: CalendarCustomEventType[] = jobsData.result.reduce(
        (acc: CalendarCustomEventType[], j: IJob) => {
          return [
            ...acc,
            ...j.jobSchedules.map((s): CalendarCustomEventType => {
              const supervisors = summariseSupervisors(s.supervisor);

              return {
                address: j.lead?.address ?? "",
                id: s.id,
                jobId: j.id,
                superVisor: supervisors.name,
                additionalSupervisors: supervisors.othersCount,
                start: moment(s.startTime).toDate(),
                end: moment(s.endTime).toDate(),
                title: j.name,
                employees: s.employees,
                // The summary above is a label and has dropped the ids; the
                // lanes need the people themselves.
                supervisors: s.supervisor ?? [],
              };
            }),
          ];
        },
        []
      );
      setEvents(schedules);
    }
  }, [jobsData]);

  const onEventDrop = useCallback(
    ({ event, start, end }: any) => {
      const payload = {
        employeeIds: event.employees.map((e: Employee) => e.id),
        startTime: start,
        endTime: end,
        id: event.id,
      };

      requests
        .put(`/job/jobSchedule/${payload.id}`, payload)
        .then(refreshSchedule)
        .catch((error: any) => {
          // Puts the optimistic move back: the server refused it.
          setEvents(events);
          // And says why. Without the message the event just snaps back and the
          // user is left guessing - and retrying.
          show({
            severity: "error",
            detail:
              error?.response?.data?.error?.message || "Something went wrong ",
          });
        });

      setEvents((prev: any) => {
        const existing = prev.find((ev: any) => ev.id === event.id) ?? {};
        const filtered = prev.filter((ev: any) => ev.id !== event.id);
        return [...filtered, { ...existing, start, end }];
      });
    },
    [setEvents, events, refreshSchedule, show]
  );

  const handleEventClick = (data: any, e: any) => {
    setSelectedEvent(data);
    //@ts-ignore
    eventPopover.current && eventPopover.current.toggle(e);
  };

  const canCreateJob = user?.roles.dcs_client.includes(UserRoles.JOB_CREATE);

  return (
    <>
      <SectionHeader title="Schedule" />
      {canCreateJob ? (
        <Button
          label="Create Job & Schedule"
          className={cx("p-button-sm", styles.createBtn)}
          onClick={() => setShowCreateJobModal(true)}
        />
      ) : null}
      <div className={styles.scheduleControls}>
        <ScheduleSearch onChange={handleSearchChange} value={searchValue} />
        <MultiSelect
          aria-label="Filter by employee"
          className={styles.employeeFilter}
          display="chip"
          filter
          maxSelectedLabels={2}
          onChange={(e) => setSelectedEmployeeIds(e.value ?? [])}
          options={employeeOptions}
          placeholder="All employees"
          selectedItemsLabel="{0} employees"
          value={selectedEmployeeIds}
        />
        <SelectButton
          aria-label="Layout"
          onChange={(e) => {
            // SelectButton reports null when the active option is clicked
            // again; a layout always has to be one of the two.
            if (e.value !== null && e.value !== undefined) {
              handleLayoutChange(e.value === "employee");
            }
          }}
          options={[
            { label: "Calendar", value: "calendar" },
            { label: "By employee", value: "employee" },
          ]}
          value={byEmployee ? "employee" : "calendar"}
        />
      </div>
      {showCreateJobModal ? ( // prime react modal always mounts, thats why so
        <CreateJobModal set={setShowCreateJobModal} show={showCreateJobModal} />
      ) : null}
      <div
        style={{
          height: `calc(100% - ${canCreateJob ? 202 : 162}px)`,
          overflow: "auto",
        }}
      >
        <DnDCalendar
          date={date}
          onNavigate={setDate}
          view={effectiveView}
          onView={setView}
          // Month and agenda cannot draw lanes, so they are taken off the
          // picker while the lanes are on rather than left there to be
          // picked and silently ignored.
          views={byEmployee ? RESOURCE_VIEWS : undefined}
          onRangeChange={handleRangeChange}
          events={byEmployee ? resourceEvents : filteredEvents}
          resources={byEmployee ? resources : undefined}
          // Spelt out rather than left to react-big-calendar's defaults,
          // which are the same keys ("id", "title", "resourceId") but are
          // only documented in its source. Functions rather than key strings
          // because the published types accept nothing else, though the
          // runtime takes either.
          resourceIdAccessor={(resource: any) => resource.id}
          resourceTitleAccessor={(resource: any) => resource.title}
          resourceAccessor={(event: any) => event.resourceId}
          localizer={localizer}
          resizable={false}
          onEventDrop={onEventDrop}
          onSelectEvent={handleEventClick}
          style={{ height: 800 }}
          popup
        />
      </div>
      <ConfirmationModal
        visible={showDeletePopup}
        onHide={() => setShowDeletePopup(false)}
        accept={() => deleteSchedule.mutate(selectedEvent?.id)}
        reject={() => setShowDeletePopup(false)}
      />
      <OverlayPanel
        ref={eventPopover}
        onHide={() => {
          setShowTrackingModal(false);
          setSelectedEvent(null);
        }}
      >
        {selectedEvent ? (
          !showTrackingModal ? (
            <ScheduleInfo
              selectedEvent={selectedEvent}
              setShowDeletePopup={setShowDeletePopup}
              setShowTrackingModal={setShowTrackingModal}
            />
          ) : (
            <TimeTrackingModal
              closeTimeTrackingModal={() => setShowTrackingModal(false)}
              selectedEvent={selectedEvent}
            />
          )
        ) : null}
      </OverlayPanel>
    </>
  );
}

export default Schedule;
